import React from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { PERSONALITY_OPTIONS } from '../../lib/onboarding/avatarConfig';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import type { CoachPersonality } from '../../lib/onboarding/unifiedTypes';

interface AvatarSelectorProps {
  selectedPersonality: CoachPersonality | null;
  onSelect: (personality: CoachPersonality) => void;
  className?: string;
}

export default function AvatarSelector({ 
  selectedPersonality, 
  onSelect, 
  className = '' 
}: AvatarSelectorProps) {
  const { isPlaying, currentAudio, isLoading, playAudio, stopAudio, preloadAudio } = useAudioPlayer();

  // Preload all audio files on mount
  React.useEffect(() => {
    const audioFiles = PERSONALITY_OPTIONS.map(option => option.audioFile);
    preloadAudio(audioFiles);
  }, [preloadAudio]);

  const handleAvatarClick = async (personality: CoachPersonality) => {
    const option = PERSONALITY_OPTIONS.find(opt => opt.id === personality);
    if (!option) return;

    // If this avatar is already selected and playing, stop it
    if (currentAudio === option.audioFile && isPlaying) {
      stopAudio();
      return;
    }

    // Play audio preview
    await playAudio(option.audioFile);
    
    // Select this personality
    onSelect(personality);
  };

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${className}`}>
      {PERSONALITY_OPTIONS.map((option) => {
        const isSelected = selectedPersonality === option.id;
        const isCurrentlyPlaying = currentAudio === option.audioFile && isPlaying;
        const isCurrentlyLoading = currentAudio === option.audioFile && isLoading;

        return (
          <button
            key={option.id}
            onClick={() => handleAvatarClick(option.id)}
            className={`
              relative p-4 rounded-2xl border-2 text-left transition-all duration-200 hover:shadow-lg
              ${isSelected 
                ? 'border-pink-400 bg-pink-50 shadow-md' 
                : 'border-gray-200 hover:border-pink-300 hover:bg-pink-25'
              }
            `}
            disabled={isCurrentlyLoading}
          >
            {/* Avatar Image */}
            <div className="relative mb-3">
              <div className="w-full aspect-square rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={option.imageFile}
                  alt={option.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = `https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop`;
                  }}
                />
              </div>
              
              {/* Audio Play Indicator */}
              <div className="absolute top-2 right-2">
                {isCurrentlyLoading ? (
                  <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : isCurrentlyPlaying ? (
                  <div className="w-8 h-8 bg-pink-400 text-white rounded-full flex items-center justify-center">
                    <Volume2 className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors">
                    <Play className="h-4 w-4 text-gray-600 ml-0.5" />
                  </div>
                )}
              </div>
            </div>

            {/* Avatar Info */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{option.name}</h3>
              <p className="text-sm text-gray-600">{option.description}</p>
            </div>

            {/* Selection Indicator */}
            {isSelected && (
              <div className="absolute top-2 left-2 w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}

            {/* Audio Playing Animation */}
            {isCurrentlyPlaying && (
              <div className="absolute inset-0 rounded-2xl border-2 border-pink-400 animate-pulse" />
            )}
          </button>
        );
      })}
    </div>
  );
}